import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { DebugModeContextValue } from './types';
import { isDebugModeActive, getDebugModeRemainingMs } from './debugModeUtils';
import { logger } from './LoggerService';
import {
  startReplayForDebugMode,
  stopReplayForDebugMode,
} from './sentryDebugMode';

const DebugModeContext = createContext<DebugModeContextValue | undefined>(
  undefined,
);

interface DebugModeProviderProps {
  children: ReactNode;
  debugModeUntil?: string | null;
  userId?: string | null;
  userEmail?: string | null;
}

/**
 * Provider component that manages debug mode state and configures the logger.
 *
 * Features:
 * - Initializes debug mode from debugModeUntil timestamp
 * - Configures logger singleton with user info
 * - Auto-disables when debug mode expires
 * - Logs activation/expiration events
 */
export function DebugModeProvider({
  children,
  debugModeUntil = null,
  userId = null,
  userEmail = null,
}: DebugModeProviderProps) {
  const [isDebugMode, setIsDebugMode] = useState(() =>
    isDebugModeActive(debugModeUntil),
  );

  // Configure logger when debug mode state or user info changes
  const configureLogger = useCallback(() => {
    const isActive = isDebugModeActive(debugModeUntil);
    logger.configure({
      isDebugModeEnabled: isActive,
      userId,
      userEmail,
    });
    return isActive;
  }, [debugModeUntil, userId, userEmail]);

  // Initial configuration and debug mode state sync
  useEffect(() => {
    const isActive = configureLogger();
    setIsDebugMode(isActive);

    if (isActive) {
      logger.info('Debug mode activated', {
        debugModeUntil,
        userId,
        userEmail,
      });
      startReplayForDebugMode();
    }
  }, [configureLogger, debugModeUntil, userId, userEmail]);

  // Set up timer for auto-expiration
  useEffect(() => {
    if (!isDebugMode) {
      return undefined;
    }

    const remainingMs = getDebugModeRemainingMs(debugModeUntil);

    if (remainingMs <= 0) {
      return undefined;
    }

    const timerId = setTimeout(() => {
      logger.info('Debug mode expired', {
        debugModeUntil,
        userId,
      });
      stopReplayForDebugMode();

      setIsDebugMode(false);
      logger.configure({
        isDebugModeEnabled: false,
        userId,
        userEmail,
      });
    }, remainingMs);

    return () => {
      clearTimeout(timerId);
    };
  }, [isDebugMode, debugModeUntil, userId, userEmail]);

  const value = useMemo<DebugModeContextValue>(
    () => ({
      isDebugMode,
      debugModeUntil: debugModeUntil ?? null,
    }),
    [isDebugMode, debugModeUntil],
  );

  return (
    <DebugModeContext.Provider value={value}>
      {children}
    </DebugModeContext.Provider>
  );
}

/**
 * Hook to access debug mode context.
 * Must be used within a DebugModeProvider.
 */
export function useDebugMode(): DebugModeContextValue {
  const context = useContext(DebugModeContext);

  if (!context) {
    throw new Error('useDebugMode must be used within a DebugModeProvider');
  }

  return context;
}
